"use client";
import { Card } from "@cmp-src/data-display/Card/Card";
import { Heading } from "@cmp-src/data-display/Heading/Heading";
import { ResourceLabel } from "@cmp-src/labels/ResourceLabel/ResourceLabel";
import { TacticLoader } from "@cmp-src/loaders/TacticLoader/TacticLoader";
import { Button } from "@cmp-src/primitives/button";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import {
  AiChatToolShowReservationInfoOutput,
  AiChatToolShowReservationInfoPart,
  CreateWorkplaceRequestParams,
  ISchedule,
  IUserFull,
  Office,
  TessaAiChatPart,
  TessaChatMessage,
  WorkplaceRequestElement
} from "@gettactic/api";
import { cn, logError, useBooleanState } from "@gettactic/helpers";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { WorkplaceRequestReserveFormData } from "@gettactic/helpers/src/reserve/mapWorkplaceRequestToReserveFormData";
import {
  BookedSchedules,
  bookedThrough,
  bookingProgress,
  dateListRule,
  lockedClashDates,
  PlannedBooking,
  planRecurringBookings,
  RecurringClashChoices,
  requestScheduleId
} from "@gettactic/helpers/src/reserve/recurringClashes";
import {
  useAuthenticated,
  useI18n,
  useTacticHooks,
  useWorkplaceRequestsAllowed
} from "@gettactic/hooks";
import { format, parseISO } from "date-fns";
import React, { useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { CheckInCardOnWeb } from "@/features/dashboard/components/CheckInCardOnWeb";
import { MapWithMiniOfficePhoto } from "@/features/dashboard/components/DashboardEventDetails/MapWithMiniOfficePhoto";
import { invalidateReservationQueries } from "@/features/reserve/components/Reserve/helpers";
import { ReserveForm } from "@/features/reserve/components/Reserve/ReserveForm";
import { useReserveModal } from "@/features/reserve/hooks/useReserveModal";
import { useReserve } from "@/features/reserve/providers/context";
import {
  noParkingResource,
  OnlineMeetingProvider,
  saveReservations
} from "@/features/reserve/utils/utils-reserve";
import { useScheduleById } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsModal/ScheduleInfoCard";
import { ChatBotToolCard } from "@/features/tessa/components/ChatBotToolCard";
import { ChatBotRecurringClashes } from "@/features/tessa/components/tools/ChatBotRecurringClashes";
import {
  UseTessaMessageStateReturn,
  useTessaMessageState
} from "@/features/tessa/components/utils/useTessaMessageState";
import {
  TessaMessageRendererProps,
  TessaResponseMessages
} from "@/features/tessa/types";
import { useWorkplaceRequestMutations } from "@/features/workplace-requests/hooks/useWorkplaceRequestMutations";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";

const getRecurringString = (reservation: Reservation) => {
  let recurring: string | undefined;
  if (reservation.recurring?.rrule && reservation.recurring?.start_date) {
    recurring = `DTSTART:${String(reservation.recurring.start_date).replace(/-/gi, "")}T080000Z\nRRULE:${reservation.recurring?.rrule}`;

    if (reservation.recurring?.end_date)
      recurring += `;UNTIL=${String(reservation.recurring.end_date).replace(/-/gi, "")}T080000Z`;
  }

  return recurring;
};

const ALLOW_INLINE_RESERVATION = false;
type Reservation = NonNullable<
  AiChatToolShowReservationInfoOutput["reservations"]
>[number];
// The resource chosen for a clashing date. Core offers alternatives of the same type in the same office, so
// one missing from the card data is booked by its ID with the original resource's office and type rather
// than silently dropping that date.
export const clashAlternative = <T extends { id: string }>(
  resourcesById: Record<string, T> | undefined,
  resourceId: string,
  original: T
): T => resourcesById?.[resourceId] ?? { ...original, id: resourceId };

// Whether Reserve still has something to do: a booking that wasn't made, or the workplace request that comes
// with the reservation and wasn't filed yet
export const hasWorkLeft = (
  missing: unknown[],
  needsRequest: boolean,
  requestCreated: boolean
) => missing.length > 0 || (needsRequest && !requestCreated);

// Saved with the message while a reservation is partly booked, so a reload or going back to the list resumes
// it instead of booking it again
export interface ReservationProgress {
  booked: BookedSchedules;
  requestCreated: boolean;
  choices: RecurringClashChoices;
}

// A done reservation's schedules are saved under its position in the proposal, so two proposals on the same
// resource don't share them
export const reservationDoneKey = (reservationKey: string | number) =>
  `reservation:${reservationKey}`;

// The schedules saved for a reservation. Messages saved before used the resource ID: read it only when that
// resource is in a single proposal, so it can't mark another one done.
export const savedScheduleIds = (
  data: Record<string, string[]> | undefined,
  reservations: { resource_id: string }[],
  index: number
): string[] => {
  const byIndex = data?.[reservationDoneKey(index)];
  if (byIndex) return byIndex;
  const resourceId = reservations[index]?.resource_id;
  if (!resourceId) return [];
  const proposals = reservations.filter(
    (r) => r.resource_id === resourceId
  ).length;
  return proposals === 1 ? (data?.[resourceId] ?? []) : [];
};

// The message state after an attempt: a finished reservation saves its schedules and drops its progress; an
// unfinished one keeps its progress. The message stays "partial" while any reservation is.
export const nextMessageState = (
  saved:
    | {
        data?: Record<string, string[]>;
        progress?: Record<string, ReservationProgress>;
      }
    | null
    | undefined,
  reservationKey: string,
  attempt: {
    finished: boolean;
    scheduleIds: string[];
    progress: ReservationProgress;
  }
) => {
  const progress = { ...(saved?.progress ?? {}) };
  const data = { ...(saved?.data ?? {}) };
  if (attempt.finished) {
    delete progress[reservationKey];
    data[reservationDoneKey(reservationKey)] = attempt.scheduleIds;
  } else {
    progress[reservationKey] = attempt.progress;
  }
  return {
    state: (Object.keys(progress).length > 0 ? "partial" : "completed") as
      | "partial"
      | "completed",
    data: { data, progress }
  };
};

// The reserve modal books one series with its own rule editor, which can't keep a list of exact dates or
// the dates moved to another resource. With clashing dates the original series would be rejected anyway, so
// the card books it instead.
export const reserveModalBlockedMessage = (hasClashes: boolean) =>
  hasClashes
    ? "Some dates can't be booked. Choose what to do with them below, then use Reserve."
    : null;

// What this session already booked per card and reservation, besides the copy saved with the message: if
// saving it failed, a remount still knows what's booked and doesn't book it again
const sessionProgress = new Map<
  string,
  { booked: BookedSchedules; requestCreated: boolean }
>();

export const restoredProgress = (
  saved: { booked?: BookedSchedules; requestCreated?: boolean } | undefined,
  session: { booked: BookedSchedules; requestCreated: boolean } | undefined
) => ({
  booked: { ...(saved?.booked ?? {}), ...(session?.booked ?? {}) },
  requestCreated: !!(saved?.requestCreated || session?.requestCreated)
});

// One reservation the card booked, rendered once loaded: its actions need the real schedule. One that can't
// be loaded, e.g. deleted elsewhere, isn't shown.
const LoadedSchedule: React.FC<{
  scheduleId: string;
  children: (schedule: ISchedule) => React.ReactNode;
}> = ({ scheduleId, children }) => {
  const loaded = useScheduleById(scheduleId);
  if (loaded.data) return children(loaded.data);
  if (loaded.isError) return null;
  return <TacticLoader className="opacity-60" />;
};

const ChatBotToolShowReservationInfoCard: React.FC<{
  reservation: Reservation;
  reservationIndex: number;
  isCompact: boolean;
  output: AiChatToolShowReservationInfoOutput;
  onSelect: (reservationIndex: number | null) => void;
  message?: TessaChatMessage | TessaResponseMessages | undefined;
  part?: TessaAiChatPart;
  messageState: UseTessaMessageStateReturn | null;
}> = ({
  reservation,
  reservationIndex,
  isCompact,
  output,
  onSelect,
  message,
  part,
  messageState
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { queryClient, api } = useTacticHooks();
  const isWorkplaceRequestsAllowed =
    useWorkplaceRequestsAllowed("workplace_requests");
  const { createWorkplaceRequest, isLoading: isWorkplaceRequestLoading } =
    useWorkplaceRequestMutations();

  const askTessa = useAskTessa();
  const { openReserveModal, openDuplicateModal } = useReserve();
  const { openEditModal, openCancelModal } = useReserveModal();
  const { templates } = useI18n(authenticatedUser);
  const isLoading = useBooleanState();
  const wpRequestsRef = useRef<WorkplaceRequestsRef | null>(null);
  // What earlier Reserve attempts made, saved with the message so a reload or going back to the list resumes it
  const savedProgress = (
    messageState?.data?.progress as
      | Record<string, ReservationProgress>
      | undefined
  )?.[String(reservationIndex)];
  const [clashChoices, setClashChoices] = useState<RecurringClashChoices>(
    savedProgress?.choices ?? {}
  );
  const sessionKey = `${part?.toolCallId ?? ""}:${reservationIndex}`;
  const restored = restoredProgress(
    savedProgress,
    sessionProgress.get(sessionKey)
  );
  // A ref too, so a quick second click sees the bookings already made
  const bookedRef = useRef<BookedSchedules>(restored.booked);
  const [booked, setBooked] = useState<BookedSchedules>(restored.booked);
  const requestCreated = useRef(restored.requestCreated);
  const rememberProgress = () =>
    sessionProgress.set(sessionKey, {
      booked: bookedRef.current,
      requestCreated: requestCreated.current
    });
  // Set before any await, so a second Reserve click while booking does nothing
  const reserving = useRef(false);
  const occurrences =
    output._metadata?.recurring_by_reservation?.[String(reservationIndex)];
  const hasClashes = !!occurrences && occurrences.clashes.length > 0;

  const resource = output._metadata?.resources_by_id?.[reservation.resource_id];

  const savedSchedules = savedScheduleIds(
    messageState?.data?.data as Record<string, string[]> | undefined,
    output.reservations ?? [],
    reservationIndex
  );
  const hasSchedules = savedSchedules.length > 0;

  const loadedSchedule = useScheduleById(savedSchedules[0] || null);

  const attenders = (reservation.user_ids || [])
    .map((userId) => {
      return output._metadata?.users_by_id?.[userId];
    })
    .filter((user) => !!user);

  if (!resource) return null;
  const isToolFullScreen = askTessa?.fullPageToolDisclosure.isOpen ?? false;
  const reservationOfficeId = resource?.office_id;
  const reservationParentId = resource.parent_id;
  const reservationType = resource.type;
  const startDate = new Date(dateStringToLocalTime(reservation.start));
  const duration = Math.floor(
    (new Date(reservation.end).getTime() -
      new Date(reservation.start).getTime()) /
      60000
  );

  const schedule =
    loadedSchedule.data ||
    ({
      id: "new-schedule",
      start: reservation.start,
      end: reservation.end,
      resource,
      slots: attenders.map((user) => ({
        user,
        resource
      })),
      description: "New reservation"
    } as ISchedule);

  const handleOpenReserveModal = async (reservation: Reservation) => {
    if (hasSchedules) {
      return;
    }

    const blockedMessage = reserveModalBlockedMessage(hasClashes);
    if (blockedMessage) {
      toast(blockedMessage);
      return;
    }
    // The modal would book the whole reservation again
    if (Object.keys(bookedRef.current).length > 0 || requestCreated.current) {
      toast(
        "Part of this reservation is already booked. Use Reserve to finish it."
      );
      return;
    }

    const resource =
      output._metadata?.resources_by_id?.[reservation.resource_id];
    const reservationOfficeId =
      output._metadata?.resources_by_id?.[reservation.resource_id]?.office_id;
    const reservationParentId =
      output._metadata?.resources_by_id?.[reservation.resource_id]?.parent_id;
    const reservationType =
      output._metadata?.resources_by_id?.[reservation.resource_id]?.type;

    let workplaceRequestItems:
      | (WorkplaceRequestReserveFormData & { isNew: boolean })[]
      | undefined;

    if (isWorkplaceRequestsAllowed && reservation.workplace_request?.type_id) {
      const workplaceRequestTypes = isWorkplaceRequestsAllowed
        ? (await api.client.workplaceRequestTypes.getWorkplaceRequestTypes())
            .result?.elements || []
        : undefined;

      const type = workplaceRequestTypes?.find(
        (t) => t.id === reservation.workplace_request?.type_id
      );

      workplaceRequestItems = [
        {
          isNew: true,
          title: reservation.workplace_request.title,
          isDeleted: false,
          originalData: {
            office_id: reservation.workplace_request.office_id
          } as WorkplaceRequestElement,
          id: undefined,
          type: type || {
            id: reservation.workplace_request.type_id,
            name: "Unknown Type",
            description: "",
            // A request already exists against this type, so it files tickets
            // whatever the (unresolvable) real type says.
            kind: "default",
            in_use: false,
            llm_context: "",
            assigned_users: [],
            category_id: "",
            office_ids: [],
            has_office_restrictions: false,
            resource_ids: [],
            has_resource_restrictions: false,
            default_auto_assignee: null,
            office_auto_assignees: {},
            statuses: [],
            fields: [],
            icon: null,
            requires_approval: false,
            close_on_approval: false,
            requires_location: false,
            require_location_at_approval: false,
            hide_description: false
          },
          description: reservation.workplace_request.description
        }
      ];
    }

    if (!reservationType) return;
    const startDate = new Date(dateStringToLocalTime(reservation.start));
    const duration = Math.floor(
      (new Date(reservation.end).getTime() -
        new Date(reservation.start).getTime()) /
        60000
    );

    const owner = reservation.organizer_user_id
      ? ({ id: reservation.organizer_user_id } as IUserFull)
      : attenders?.length === 1 &&
          attenders[0]?.id !== authenticatedUser?.user?.id
        ? attenders[0]
        : undefined;

    const recurring = getRecurringString(reservation);

    openReserveModal({
      start: startDate,
      time: startDate,
      duration,
      floorId: reservationParentId,
      title: reservation.title,
      description: reservation.description,
      resourceType: reservationType,
      owner,
      office: reservationOfficeId
        ? ({
            id: reservationOfficeId
          } as Office)
        : undefined,
      resource: resource,
      attenders,
      // Saved like a reservation booked from the card, so it shows as reserved and can't be booked again
      onSuccess: (scheduleIds) => {
        if (!messageState) return;
        const next = nextMessageState(
          messageState.data as Parameters<typeof nextMessageState>[0],
          String(reservationIndex),
          {
            finished: true,
            scheduleIds,
            progress: { booked: {}, requestCreated: false, choices: {} }
          }
        );
        messageState.setMessageState(next.state, next.data);
      },
      workplaceRequestItems,
      isAdvancedRecurringOpenByDefault: !!recurring,
      recurring,
      onlineMeetingProvider:
        (reservation.online_meeting_provider as OnlineMeetingProvider) ||
        undefined
    });
  };

  const handleOpenEditModal = (schedule: ISchedule) => {
    openEditModal(schedule, true);
  };

  const handleOpenCancelModal = (schedule: ISchedule) => {
    openCancelModal([schedule]);
  };

  // Books one reservation; a recurring one when given a rule
  const book = async (
    reservation: Reservation,
    resourceToBook: NonNullable<typeof resource>,
    start: string,
    recurring: string
  ) => {
    const authUserId = authenticatedUser?.user?.id;
    const offices = authenticatedUser.offices?.offices ?? [];
    if (!authUserId) return [];
    const currentOfficeId = resourceToBook.office_id;
    const isMeetingRoom = resourceToBook.type === "meeting_room";
    const startDate = new Date(dateStringToLocalTime(start));
    const duration = Math.floor(
      (new Date(reservation.end).getTime() -
        new Date(reservation.start).getTime()) /
        60000
    );

    const office = offices.find((office) => office.id === currentOfficeId);
    if (!office) {
      throw new Error(
        `Office with id ${currentOfficeId} not found in user offices`
      );
    }

    const data = {
      time: startDate,
      duration: { durationLabel: "", value: duration },
      resource: resourceToBook,
      users: attenders.map((user) => ({ value: user.id || "" })) || [],
      teams: [],
      recurring,
      organizer: reservation.organizer_user_id
        ? { value: reservation.organizer_user_id }
        : { value: "" },
      parking_resource: null,
      weekDates: [startDate],
      title: reservation.title,
      description: reservation.description,
      online_meeting: reservation.online_meeting_provider
        ? {
            provider:
              reservation.online_meeting_provider as OnlineMeetingProvider
          }
        : undefined
    };

    const ownerUserId = reservation.user_ids?.[0];
    const { hasError, message, scheduleIds } = await saveReservations(data, {
      organizerId: data.organizer?.value ?? null,
      isMeetingRoom,
      authUserId: ownerUserId || authUserId,
      office,
      // allowOthers && the admin has chosen a user
      allowOthers: true,
      timeZone: office.time_zone,
      officeId: currentOfficeId,
      noParkingResource,
      dateFormat: templates.displayDate,
      orgSlug: authenticatedUser.slug || ""
    });
    if (hasError && message) {
      toast.error(message);
    }
    return scheduleIds ?? [];
  };

  // The reservations that book this one, with its clashing dates skipped or moved to another resource
  const planFor = (reservation: Reservation) =>
    planRecurringBookings(
      {
        resourceId: reservation.resource_id,
        start: reservation.start,
        end: reservation.end
      },
      occurrences,
      clashChoices
    );

  const bookingRequest = (
    reservation: Reservation,
    booking: PlannedBooking
  ) => ({
    resource: booking.moved
      ? clashAlternative(
          output._metadata?.resources_by_id,
          booking.resourceId,
          resource
        )
      : resource,
    recurring:
      booking.recurrence === "original"
        ? getRecurringString(reservation) || ""
        : booking.recurrence === "dates"
          ? dateListRule(booking.dates)
          : ""
  });

  const saveProgress = async (attempt: {
    finished: boolean;
    scheduleIds: string[];
  }) => {
    if (!messageState) return;
    const next = nextMessageState(
      messageState.data as Parameters<typeof nextMessageState>[0],
      String(reservationIndex),
      {
        ...attempt,
        progress: {
          booked: bookedRef.current,
          requestCreated: requestCreated.current,
          choices: clashChoices
        }
      }
    );
    try {
      await messageState.setMessageState(next.state, next.data);
    } catch (err) {
      logError(err);
    }
  };

  // Every date clashes and all of them are skipped
  const nothingToBook = !!resource && planFor(reservation).length === 0;
  // The series goes on past the checked dates and some clash, so it's booked through the last checked one
  const throughDate = bookedThrough(occurrences);

  const reserveResource = async (reservation: Reservation) => {
    if (!resource || !authenticatedUser?.user?.id) return;
    const plan = planFor(reservation);
    if (plan.length === 0) return;
    const workplaceRequestData = reservation.workplace_request;
    const needsRequest =
      isWorkplaceRequestsAllowed &&
      !!workplaceRequestData?.type_id &&
      !!workplaceRequestData?.office_id;
    const { missing } = bookingProgress(bookedRef.current, plan);
    if (
      !hasWorkLeft(missing, needsRequest, requestCreated.current) ||
      reserving.current
    )
      return;
    reserving.current = true;
    isLoading.true();

    try {
      // Each booking is independent: one failing doesn't stop the others, and a retry only books what's missing
      for (const booking of missing) {
        const request = bookingRequest(reservation, booking);
        let ids: string[] = [];
        try {
          ids = await book(
            reservation,
            request.resource,
            booking.start,
            request.recurring
          );
        } catch (err) {
          logError(err);
        }
        if (ids.length > 0) {
          bookedRef.current = { ...bookedRef.current, [booking.key]: ids };
          setBooked(bookedRef.current);
          rememberProgress();
        }
      }

      // The request goes with the series (or the one booking left), never with a date moved elsewhere
      const scheduleId = requestScheduleId(bookedRef.current, plan);
      if (
        needsRequest &&
        !requestCreated.current &&
        scheduleId &&
        workplaceRequestData?.type_id &&
        workplaceRequestData.office_id
      ) {
        const requestData: CreateWorkplaceRequestParams = {
          title: workplaceRequestData.title,
          description: workplaceRequestData.description,
          type_id: workplaceRequestData.type_id,
          office_id: workplaceRequestData.office_id,
          status: "open",
          schedule_id: scheduleId
        };

        try {
          await createWorkplaceRequest(requestData);
          // Only once it's filed, so a retry files it after a failure
          requestCreated.current = true;
          rememberProgress();
        } catch (error) {
          console.error("Failed to create workplace request:", error);
        }
      }

      const progress = bookingProgress(bookedRef.current, plan);
      const finished =
        progress.complete &&
        !hasWorkLeft(progress.missing, needsRequest, requestCreated.current);
      if (progress.scheduleIds.length > 0) {
        await invalidateReservationQueries(queryClient);
      }
      if (finished || progress.scheduleIds.length > 0) {
        await saveProgress({ finished, scheduleIds: progress.scheduleIds });
      }
      if (!finished) {
        toast.error(
          progress.complete
            ? "The workplace request couldn't be created. Use Reserve to try again."
            : "Some dates couldn't be booked. Use Reserve to try them again."
        );
      }
    } catch (err) {
      logError(err);
    } finally {
      reserving.current = false;
      isLoading.false();
    }
  };

  // Dates already booked on another resource, so their choice can't change and book them twice
  const bookedDates = lockedClashDates(booked);

  // A date moved to another resource shows that resource
  const bookingCard = (booking: ISchedule) => {
    const bookedResource =
      output._metadata?.resources_by_id?.[booking.resource?.id ?? ""] ??
      resource;
    return (
      <CheckInCardOnWeb
        reservation={{
          ...booking,
          resource: {
            ...bookedResource,
            floor: {
              id: bookedResource.parent_id || "",
              name: ""
            },
            areas: []
          },
          joinUrl: ""
        }}
        onEdit={async () => {
          await handleOpenEditModal(booking);
        }}
        onCancel={async () => {
          await handleOpenCancelModal(booking);
        }}
        onDuplicate={async () => {
          setTimeout(() => {
            void openDuplicateModal(booking);
          }, 200);
        }}
      />
    );
  };

  // Every reservation booked for this proposal: the series and each date moved to another resource, each
  // checked in to and cancelled on its own
  const checkInCard = hasSchedules ? (
    <div className="flex flex-col gap-2">
      {savedSchedules.map((scheduleId) => (
        <LoadedSchedule key={scheduleId} scheduleId={scheduleId}>
          {bookingCard}
        </LoadedSchedule>
      ))}
    </div>
  ) : (
    <div className={"p-2"}>
      <ResourceLabel
        resource={resource}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleOpenReserveModal(reservation);
        }}
        schedules={[schedule]}
        allowFavorite={false}
        showAttenders
      />
    </div>
  );

  return (
    <div
      key={reservation.resource_id}
      className={cn(
        "min-w-[400px]",
        isCompact && "rounded-xl bg-neutral-50 p-2"
      )}
    >
      {!isCompact && !!reservationOfficeId && !!reservationParentId && (
        <div className={"rounded-xl bg-neutral-50 p-2"}>
          <Card
            className={
              "borer-neutral-300 w-full cursor-pointer hover:border-1 p-0 border-0"
            }
            contentProps={{ className: "p-0" }}
            hasShadow={false}
          >
            <div className={"mt-2 border-t border-t-neutral-200 px-0"}>
              {checkInCard}
            </div>
            <MapWithMiniOfficePhoto
              resourceId={reservation.resource_id}
              officeId={reservationOfficeId}
              parentId={reservationParentId}
              startDate={reservation.start.split("T")[0]}
              resourceImage={undefined}
              showMiniMap={false}
              addMainClassName={!isToolFullScreen}
              className={
                isToolFullScreen
                  ? "absolute inset-0 top-40 border-t"
                  : undefined
              }
            />
          </Card>
        </div>
      )}
      {!isCompact &&
        ALLOW_INLINE_RESERVATION &&
        !!reservationType &&
        !!reservationOfficeId && (
          <ReserveForm
            reserveModal={true}
            isEmbedded={false}
            onClose={() => null}
            start={startDate}
            time={startDate}
            resource={
              output._metadata?.resources_by_id?.[reservation.resource_id]
            }
            duration={duration}
            floorId={reservationParentId}
            resourceType={reservationType}
            office={
              {
                id: reservationOfficeId
              } as Office
            }
            attenders={attenders}
            wpRequestsRef={wpRequestsRef}
          />
        )}
      {isCompact && (
        <Card
          className={
            "borer-neutral-300 w-full cursor-pointer p-0 hover:border-1"
          }
          contentProps={{ className: "p-0" }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleOpenReserveModal(reservation);
          }}
          hasShadow={!hasSchedules}
          isFlat={hasSchedules}
        >
          {checkInCard}
        </Card>
      )}
      {!hasSchedules && occurrences && (
        <ChatBotRecurringClashes
          occurrences={occurrences}
          resourceName={resource.name}
          resourcesById={output._metadata?.resources_by_id ?? {}}
          choices={clashChoices}
          onChange={(date, resourceId) =>
            setClashChoices((current) => ({ ...current, [date]: resourceId }))
          }
          bookedDates={bookedDates}
          disabled={isLoading.state}
        />
      )}
      {!hasSchedules &&
        !ALLOW_INLINE_RESERVATION &&
        !!reservationType &&
        !!reservationOfficeId && (
          <div className={"mt-4 flex w-full flex-col gap-2"}>
            {nothingToBook && (
              <p className="text-neutral-600 text-sm">
                Every date is skipped, so there's nothing to book. Choose
                another resource for a date to book it.
              </p>
            )}
            <Button
              className={"w-full"}
              onClick={() => reserveResource(reservation)}
              isLoading={isLoading.state}
              disabled={nothingToBook}
            >
              {throughDate
                ? `Reserve through ${format(parseISO(throughDate), "EEE, MMM d")}`
                : "Reserve"}
            </Button>
          </div>
        )}
    </div>
  );
};

export const ChatBotToolShowReservationInfo: React.FC<
  TessaMessageRendererProps<AiChatToolShowReservationInfoPart>
> = ({ part, message }) => {
  const output = JSON.parse(part?.output || "null") as
    | AiChatToolShowReservationInfoOutput
    | undefined;

  const askTessa = useAskTessa();

  // By position in the response: the output is parsed again on every render, so objects don't keep their identity
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

  const messageState = useTessaMessageState(
    askTessa?.selectedChatId,
    message,
    part
  );

  if (!output) return <TacticLoader className={"opacity-60"} />;

  const reservations = output.reservations ?? [];
  const selectedReservation =
    selectedIndex === null ? null : (reservations[selectedIndex] ?? null);

  const getReservation = (reservationIndex: number, isCompact = false) => {
    const reservation = reservations[reservationIndex];
    if (!reservation) return null;
    return (
      <ChatBotToolShowReservationInfoCard
        // Keyed by the reservation, so a card's clash choices don't move to another one between lists
        key={reservationIndex}
        reservation={reservation}
        reservationIndex={reservationIndex}
        isCompact={isCompact}
        output={output}
        onSelect={setSelectedIndex}
        message={message}
        part={part}
        messageState={messageState}
      />
    );
  };

  const isScheduled = (index: number) =>
    savedScheduleIds(
      messageState?.data?.data as Record<string, string[]> | undefined,
      reservations,
      index
    ).length > 0;
  const indexes = reservations.map((_, index) => index);
  const availableReservations = indexes.filter((index) => !isScheduled(index));
  const scheduledReservations = indexes.filter(isScheduled);

  return (
    <ChatBotToolCard toolId={part.toolCallId}>
      {messageState?.state === "cancelled" && "Reservation cancelled."}
      {
        <>
          {output?.reservations?.length === 1 &&
            !selectedReservation &&
            getReservation(0)}
          {(output?.reservations?.length || 0) > 1 && selectedReservation && (
            <div>
              <div>
                <Button
                  variant={"link"}
                  className={"mb-4 p-0"}
                  onClick={() => setSelectedIndex(null)}
                >
                  &larr; Choose another resource
                </Button>
              </div>
              {selectedIndex !== null && getReservation(selectedIndex)}
            </div>
          )}
          {(output?.reservations?.length || 0) > 1 && !selectedReservation && (
            <div className={"w-full"}>
              <Heading as={"h3"} className={"mb-6"}>
                I found {output?.reservations?.length} suitable resources
              </Heading>
              {(scheduledReservations?.length || 0) > 0 && (
                <>
                  <div>You have reserved this resources</div>
                  <div
                    className={"grid w-full grid-cols-2 flex-wrap gap-4 py-4"}
                  >
                    {scheduledReservations.map((index) =>
                      getReservation(index, true)
                    )}
                  </div>
                </>
              )}
              {(availableReservations?.length || 0) > 0 && (
                <>
                  <div>Available resources</div>
                  <div
                    className={"grid grid-cols-2 w-full flex-wrap gap-4 py-4"}
                  >
                    {availableReservations.map((index) =>
                      getReservation(index, true)
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      }
    </ChatBotToolCard>
  );
};
